import React, { useEffect, useState } from 'react';
import { X, GitBranch, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../../component-styles/RecordPages.css';
import API_BASE from '../../../utils/api.js';
import notify from '../../../utils/notify';

export default function ItemFolioModal({ item, projectId, onClose, onEdit, onDelete }) {
  const userId = localStorage.getItem('userId');

  const [usedIn, setUsedIn] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const itemKey = item?.sk || item?.itemId || item?.id;
    if (!itemKey || !userId) return;

    setLoading(true);
    setError('');

    const url = `${API_BASE}/api/library/${encodeURIComponent(userId)}/folio/${encodeURIComponent(
      itemKey
    )}${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setUsedIn(data);
          return;
        }
        const list =
          (Array.isArray(data.usedIn) && data.usedIn) ||
          (Array.isArray(data.links) && data.links) ||
          (Array.isArray(data.references) && data.references) ||
          [];
        setUsedIn(list);
      })
      .catch((err) => {
        console.error('Failed to load item folio:', err);
        setUsedIn([]);
        setError('Failed to load folio links.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [item, userId, projectId]);

  async function handleDelete() {
    const itemId = item?.sk || item?.itemId;
    if (!itemId) return;

    setDeleting(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/library/${encodeURIComponent(userId)}/items/${encodeURIComponent(itemId)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Delete failed');
      onDelete?.(itemId);
      onClose?.();
    } catch (err) {
      console.error('Failed to delete item:', err);
      notify('Failed to delete item.');
    } finally {
      setDeleting(false);
    }
  }

  if (!item) return null;

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {item.name}
          {item.category && <span className="folio-sub">{item.category}</span>}
        </h3>

        {/* ACTION BUTTONS */}
        {(onEdit || onDelete) && (
          <div className="folio-item-actions">
            {onEdit && (
              <button
                className="folio-action-btn folio-action-edit"
                onClick={() => {
                  onClose?.();
                  onEdit(item);
                }}
              >
                <Pencil size={14} />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                className="folio-action-btn folio-action-delete"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        )}

        {/* DELETE CONFIRMATION */}
        {confirmDelete && (
          <div className="folio-delete-confirm">
            {usedIn.length > 0 ? (
              <p>
                This item appears in <strong>{usedIn.length}</strong> document
                {usedIn.length === 1 ? '' : 's'}. Deleting it from your library will not remove it
                from those documents.
              </p>
            ) : (
              <p>Delete this item from your library? This cannot be undone.</p>
            )}
            <div className="folio-delete-confirm-actions">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="folio-delete-confirm-yes"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        )}

        {/* SUMMARY */}
        <div className="folio-summary">
          <div>
            <label>Trade</label>
            <div>${Number(item.tradePrice ?? 0).toFixed(2)}</div>
          </div>
          <div>
            <label>Client</label>
            <div>${Number(item.clientUnit ?? item.pricePerUnit ?? 0).toFixed(2)}</div>
          </div>
          {item.vendor && (
            <div>
              <label>Vendor</label>
              <div>{item.vendor}</div>
            </div>
          )}
          {item.sku && (
            <div>
              <label>SKU</label>
              <div>{item.sku}</div>
            </div>
          )}
        </div>

        {/* PRODUCT LINK */}
        {item.productUrl && (
          <a
            href={item.productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="folio-product-link"
          >
            <ExternalLink size={14} />
            View Product Page
          </a>
        )}

        {/* USAGE */}
        <h4 className="folio-section-label">Appears In</h4>

        <div className="folio-applied-list">
          {loading && <div className="folio-empty">Loading usage...</div>}

          {!loading && error && <div className="folio-empty">{error}</div>}

          {!loading && usedIn.length === 0 && (
            <div className="folio-empty">Not used anywhere yet.</div>
          )}

          {!loading &&
            usedIn.map((ref, idx) => {
              const refType = (ref.type || ref.docType || '').toLowerCase();
              const refProject = ref.projectId || ref.project || projectId;
              const refId = ref.id || ref.docId || ref.invoiceId || ref.proposalId || ref.poId;
              const refLabel = ref.label || ref.docNumber || ref.name || refId || 'View';

              let url = '#';
              if (refType === 'proposal') {
                url = `/projects/${refProject}/proposals/${refId}`;
              } else if (
                refType === 'purchase-order' ||
                refType === 'purchase order' ||
                refType === 'po'
              ) {
                url = `/projects/${refProject}/purchase-orders/${refId}`;
              } else if (refType === 'invoice') {
                url = `/projects/${refProject}/invoices/${refId}`;
              } else if (refType === 'retainer') {
                url = `/projects/${refProject}/retainers/${refId}`;
              } else if (refType === 'credit') {
                url = `/projects/${refProject}/credits`;
              } else if (refType === 'timebilling' || refType === 'time billing') {
                url = `/projects/${refProject}/timebilling-invoices/${refId}`;
              }

              return (
                <Link to={url} key={idx} className="folio-applied-row">
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{refLabel}</b> - {refType ? refType.replace('-', ' ') : 'linked'}
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              );
            })}
        </div>
      </div>
    </div>
  );
}
